import React, { useCallback, useMemo } from 'react'
import { FormProvider, UseFormReturn } from 'react-hook-form'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { Panel } from '@components/Panel/Panel'
import { FullPanelOverlay } from '@components/Web3/WalletLinkingPanel'
import { useQueryClient } from '@tanstack/react-query'
import { Box, ErrorMessage, FancyButton, FormRender, Icon, Stack, Text, TextField } from '@ui'
import { useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { TransactionStatus, useMembershipPrice } from 'use-towns-client'
import { parseUnits } from 'viem'

import { formatDecimalValue } from '../utils'

import { UpgradeToUsdcSchema, upgradeToUsdcSchema } from './upgradeToUsdcSchema'
import { useUpgradeToUsdc } from './useUpgradeToUsdc'

export const UpgradeToUsdcPanel = React.memo(() => {
    const spaceId = useSpaceIdFromPathname()

    if (!spaceId) {
        return <></>
    }

    return <UpgradeToUsdcPanelContent spaceId={spaceId} />
})

function UpgradeToUsdcPanelContent(props: { spaceId: string }) {
    const { spaceId } = props

    return (
        <Panel label="Upgrade to USDC" padding="none" data-testid="upgrade-to-usdc-panel">
            <Stack padding height="100%">
                <UpgradeToUsdcForm spaceId={spaceId} />
            </Stack>
        </Panel>
    )
}

function UpgradeToUsdcForm(props: { spaceId: string }) {
    const { spaceId } = props
    const { execute: upgradeToUsdc, isLoading: isLoadingUpgrade } = useUpgradeToUsdc()
    const queryClient = useQueryClient()
    const { closePanel } = usePanelActions()

    return (
        <FormRender<UpgradeToUsdcSchema>
            gap
            schema={upgradeToUsdcSchema}
            defaultValues={{
                amount: '',
            }}
            id="UpgradeToUsdc"
            mode="onChange"
            height="100%"
        >
            {(form: UseFormReturn<UpgradeToUsdcSchema>) => {
                return (
                    <FormProvider {...form}>
                        {/* Placeholder content box */}
                        <Box
                            horizontal
                            padding="md"
                            background="level2"
                            rounded="sm"
                            gap="sm"
                            alignItems="center"
                        >
                            <Icon type="usdc" color="gray2" size="square_lg" />
                            <Box gap="sm">
                                <Text strong size="lg">
                                    Upgrade your Town to USDC pricing
                                </Text>
                                <Text color="gray2" size="sm">
                                    Towns has moved from ETH to USDC. Update to make sure members
                                    can continue to join your town.
                                </Text>
                            </Box>
                        </Box>

                        {/* Amount input field */}
                        <Stack gap>
                            <Text strong>Confirm Price</Text>
                            <TextField
                                autoFocus
                                background="level2"
                                placeholder="Enter USDC amount"
                                type="text"
                                inputMode="decimal"
                                {...form.register('amount', {
                                    onChange: (e) => {
                                        // Only allow numbers and a single decimal point
                                        const value = e.target.value
                                        const filtered = formatDecimalValue(value)
                                        e.target.value = filtered
                                    },
                                })}
                            />
                            <ErrorMessage
                                preventSpace
                                errors={form.formState.errors}
                                fieldName="amount"
                            />
                        </Stack>

                        <SubmitButton
                            form={form}
                            upgradeToUsdc={upgradeToUsdc}
                            onSuccess={() => {
                                closePanel()
                                popupToast(({ toast }) => (
                                    <StandardToast.Success
                                        toast={toast}
                                        message="Your town has been upgraded and now accepts USDC instead of ETH."
                                    />
                                ))
                                // Invalidate the membership info query to update button visibility
                                queryClient.invalidateQueries({
                                    queryKey: ['membershipInfo', spaceId],
                                })
                            }}
                            onError={(error) => {
                                popupToast(({ toast }) => (
                                    <StandardToast.Error
                                        toast={toast}
                                        message={error.message || 'Failed to upgrade to USDC'}
                                    />
                                ))
                            }}
                        />

                        {isLoadingUpgrade && <FullPanelOverlay withSpinner />}
                    </FormProvider>
                )
            }}
        </FormRender>
    )
}

function SubmitButton(props: {
    form: UseFormReturn<UpgradeToUsdcSchema>
    upgradeToUsdc: ReturnType<typeof useUpgradeToUsdc>['execute']
    onSuccess: () => void
    onError: (error: Error) => void
}) {
    const { form, upgradeToUsdc, onSuccess, onError } = props

    const amount = form.watch('amount')
    const spaceId = useSpaceIdFromPathname()
    const { data: membershipPrice, isLoading: isLoadingMembershipPrice } = useMembershipPrice({
        spaceId,
    })
    const isDisabled = useMemo(() => {
        return (
            form.formState.isSubmitting ||
            !amount ||
            form.formState.errors.amount != null ||
            isLoadingMembershipPrice
        )
    }, [
        form.formState.isSubmitting,
        amount,
        form.formState.errors.amount,
        isLoadingMembershipPrice,
    ])
    const currentPrice = membershipPrice ? membershipPrice.toBigInt() : 0n

    const onValid = useCallback(
        async (data: UpgradeToUsdcSchema, getSigner: GetSigner) => {
            if (isLoadingMembershipPrice) {
                return
            }
            if (!spaceId) {
                throw new Error('Space ID not found')
            }
            const signer = await getSigner()
            if (!signer) {
                createPrivyNotAuthenticatedNotification()
                return
            }
            try {
                const usdcAmount = parseUnits(data.amount, 6)

                const result = await upgradeToUsdc({
                    signer,
                    data: {
                        usdcAmount,
                        spaceId,
                        currentPrice,
                    },
                })

                if (result?.status === TransactionStatus.Success) {
                    onSuccess()
                } else {
                    onError(new Error(result?.error?.message || 'Failed to upgrade to USDC'))
                }
            } catch (error) {
                onError(error instanceof Error ? error : new Error('An unexpected error occurred'))
            }
        },
        [upgradeToUsdc, onSuccess, onError, spaceId, currentPrice, isLoadingMembershipPrice],
    )

    return (
        <>
            <Stack grow />
            <WalletReady>
                {({ getSigner }) => (
                    <FancyButton
                        borderRadius="lg"
                        cta={!isDisabled}
                        data-testid="submit-button"
                        disabled={isDisabled}
                        onClick={form.handleSubmit((data) => onValid(data, getSigner))}
                    >
                        {form.formState.isSubmitting ? 'Upgrading...' : 'Upgrade to USDC'}
                    </FancyButton>
                )}
            </WalletReady>
        </>
    )
}
